
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthHandlers } from "@/hooks/useAuthHandlers";
import { ArrowRight } from "lucide-react";

interface LoginSignupFormProps {
  isLogin: boolean;
  onToggleMode: () => void;
  onSuccess: (message?: string) => void;
  onError: (error: string) => void;
  onForgotPassword: (email: string) => void;
}

const LoginSignupForm = ({ 
  isLogin, 
  onToggleMode, 
  onSuccess, 
  onError, 
  onForgotPassword 
}: LoginSignupFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const { loading, setLoading, handleLogin, handleSignup, handleForgotPassword, handleResendConfirmation } = useAuthHandlers();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await handleLogin(email, password);
        onSuccess();
      } else {
        // Get pending favorite provider from localStorage for signup
        const favoriteProviderId = localStorage.getItem('pendingFavoriteProvider');
        const message = await handleSignup(email, password, firstName, lastName, favoriteProviderId);
        onSuccess(message);
      }
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = async () => {
    setLoading(true);
    try {
      const message = await handleForgotPassword(email);
      onSuccess(message);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmationClick = async () => {
    setLoading(true);
    try {
      const message = await handleResendConfirmation(email);
      onSuccess(message);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-5">
        {!isLogin && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm font-medium text-foreground">
                First name
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="h-12 bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-foreground/20"
                required={!isLogin}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm font-medium text-foreground">
                Last name
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="h-12 bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-foreground/20"
                required={!isLogin}
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-foreground">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-foreground/20"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-foreground">
            Password
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-foreground/20"
            required
          />
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-foreground hover:bg-foreground/90 text-background font-medium rounded-xl"
          disabled={loading}
        >
          {loading ? (
            "Please wait..."
          ) : (
            <span className="flex items-center justify-center gap-2">
              {isLogin ? "Sign in" : "Create account"}
              <ArrowRight className="h-4 w-4" />
            </span>
          )}
        </Button>
      </form>

      {/* Secondary actions */}
      {isLogin && (
        <div className="flex items-center justify-center gap-4 text-sm">
          <button
            type="button"
            onClick={handleForgotPasswordClick}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            Forgot password?
          </button>
          <span className="text-muted-foreground/50">•</span>
          <button
            type="button"
            onClick={handleResendConfirmationClick}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            Resend email
          </button>
        </div>
      )}

      {/* Toggle mode */}
      <div className="pt-4 border-t border-border">
        <p className="text-center text-sm text-muted-foreground">
          {isLogin ? "New here? " : "Already have an account? "}
          <button
            type="button"
            onClick={onToggleMode}
            className="text-foreground font-medium hover:underline"
          >
            {isLogin ? "Create an account" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginSignupForm;
